import { z } from 'zod';
import { stringOr } from '@/core/api/zod-helpers';
import { formatNumber } from '@/core/format/numbers';

const person = z
  .object({ first_name: stringOr(), last_name: stringOr(), mobile: stringOr(), national_code: stringOr() })
  .transform((p) => ({ name: `${p.first_name} ${p.last_name}`.trim(), mobile: p.mobile, nationalCode: p.national_code }));

/** The tracking endpoint sends the driver as an object; other endpoints send a plain name. */
const driver = z
  .union([
    z.object({ first_name: stringOr(), last_name: stringOr(), mobile_number: stringOr() }).transform((d) => ({ name: `${d.first_name} ${d.last_name}`.trim(), mobile: d.mobile_number })),
    z.string().transform((name) => ({ name, mobile: '' })),
  ])
  .nullish()
  .transform((d) => (d && (d.name || d.mobile) ? d : null));

const pkg = z
  .object({ package_type_title: stringOr(), count: z.coerce.number().default(0), weight: z.coerce.number().default(0) })
  .transform((p) => ({ title: p.package_type_title, count: p.count, weight: p.weight }));

/** Backend `status` values: the tracking API's upper-case enum plus the lower-case ones the mobile app uses. */
export const STATUS_LABEL: Record<string, string> = {
  PENDING: 'در انتظار',
  ASSIGNED: 'تخصیص راننده',
  PICKED_UP: 'در حال بارگیری',
  DELIVERED_WAREHOUSE: 'تحویل به انبار',
  DELIVERED_DOOR: 'تحویل درب منزل',
  CANCELLED: 'لغو شده',
  created: 'ثبت شده',
  accepted: 'پذیرفته شده',
  in_transit: 'در حال حمل',
  delivered: 'تحویل شده',
  canceled: 'لغو شده',
};
export const DELIVERY_LABEL: Record<string, string> = { STATION: 'ترمینال', TO_DOOR: 'درب منزل', normal: 'عادی', express: 'اکسپرس', vip: 'ویژه' };
export const PAYMENT_LABEL: Record<string, string> = { PREPAID: 'پیش‌پرداخت', POSTPAID: 'پس‌پرداخت', online: 'پرداخت آنلاین', cash: 'پرداخت نقدی', card: 'کارت به کارت' };

const CANCELLED = new Set(['CANCELLED', 'canceled']);
const DELIVERED = new Set(['DELIVERED_WAREHOUSE', 'DELIVERED_DOOR', 'delivered']);

/** How far a load has progressed: 0 registered · 1 driver assigned · 2 picked up · 3 delivered. */
function stage(status: string): number {
  if (DELIVERED.has(status)) return 3;
  if (status === 'PICKED_UP' || status === 'in_transit') return 2;
  if (status === 'ASSIGNED' || status === 'accepted') return 1;
  return 0;
}

export interface HistoryStep { label: string; done: boolean }

export function buildHistory(status: string, toDoor: boolean): HistoryStep[] {
  if (CANCELLED.has(status)) return [{ label: 'ثبت سفارش', done: true }, { label: 'سفارش لغو شد', done: true }];
  const s = stage(status);
  return [
    { label: 'ثبت سفارش', done: true },
    { label: 'تخصیص راننده', done: s >= 1 },
    { label: 'بارگیری مرسوله', done: s >= 2 },
    { label: toDoor ? 'تحویل درب خانه' : 'تحویل به مقصد', done: s >= 3 },
  ];
}

/**
 * The old tracking site printed prices as Rial (`price` as received). The mobile app shows Toman.
 * Flip this if the backend confirms the other unit.
 */
export const LOAD_PRICE_IN_RIAL = true;
export const formatLoadPrice = (n: number) => `${formatNumber(n)} ${LOAD_PRICE_IN_RIAL ? 'ریال' : 'تومان'}`;

export const loadSchema = z
  .object({
    id: z.number().default(0),
    load_code: stringOr(),
    sender_info: person.nullish(),
    receiver_info: person.nullish(),
    sender_name: stringOr(),
    sender_phone: stringOr(),
    receiver_name: stringOr(),
    receiver_phone: stringOr(),
    destination_city_name: stringOr(),
    delivery_address: stringOr(),
    delivery_type: stringOr(),
    urgency: stringOr(),
    price: z.coerce.number().default(0),
    payment_method: stringOr(),
    fragile: z.boolean().default(false),
    insurance: z.boolean().default(false),
    description: stringOr(),
    driver: driver,
    packages: z.array(pkg).nullish(),
    status: stringOr(),
    created_at: stringOr(),
  })
  .transform((l) => {
    const empty = { name: '', mobile: '', nationalCode: '' };
    return {
      id: l.id,
      code: l.load_code,
      sender: l.sender_info ?? { ...empty, name: l.sender_name, mobile: l.sender_phone },
      receiver: l.receiver_info ?? { ...empty, name: l.receiver_name, mobile: l.receiver_phone },
      destination: l.destination_city_name,
      address: l.delivery_address,
      delivery: DELIVERY_LABEL[l.delivery_type] ?? l.delivery_type,
      urgent: l.urgency.toUpperCase() === 'URGENT',
      urgency: l.urgency,
      price: l.price,
      payment: PAYMENT_LABEL[l.payment_method] ?? l.payment_method,
      fragile: l.fragile,
      insurance: l.insurance,
      description: l.description,
      driver: l.driver,
      packages: l.packages ?? [],
      status: l.status,
      statusLabel: STATUS_LABEL[l.status] ?? (l.status || 'نامشخص'),
      history: buildHistory(l.status, l.delivery_type === 'TO_DOOR'),
      createdAt: l.created_at,
    };
  });

export type Load = z.output<typeof loadSchema>;

export const canCancelLoad = (l: Load) => !CANCELLED.has(l.status) && !DELIVERED.has(l.status);

export const loadTone = (l: Load) =>
  DELIVERED.has(l.status) ? ('success' as const) : CANCELLED.has(l.status) ? ('danger' as const) : stage(l.status) > 0 ? ('info' as const) : ('warning' as const);

/** Which side of the load a mobile number is (used by the "search by mobile" results). */
export function loadRole(l: Load, mobile: string): 'sender' | 'receiver' | null {
  if (!mobile) return null;
  if (l.sender.mobile === mobile) return 'sender';
  if (l.receiver.mobile === mobile) return 'receiver';
  return null;
}
